import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import moment from "moment";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import GlobalApi from "../../context/api/GlobalApi";
import Api from "./Api";
import clsxm from "../../context/clsxm";
import { Select, MenuItem, FormControl } from "@mui/material";

const PAGE_SIZE = 12;

export default function SSoft_Blogs() {
  const activeLang = useSelector((s) => s.content.activeLanguage);

  const [boxContent, setBoxContent] = useState(null);
  const [textContent, setTextContent] = useState([]);
  const [widgetId, setWidgetId] = useState(null);
  const [listCategory, setListCategory] = useState([]);
  const [placeHolder, setPlaceHolder] = useState("");

  const [searchTerm, setSearchTerm] = useState(""); // controlled input
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(""); // used for API
  const [filter, setFilter] = useState({
    category: "",
  });

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [articleCount, setArticleCount] = useState(0);
  const [newsDetailPath, setNewsDetailPath] = useState("/");

  const pageCount = useMemo(
    () => Math.ceil((parseInt(articleCount || 0, 10) || 0) / PAGE_SIZE),
    [articleCount]
  );

  const getSectionsDetail = useCallback(async () => {
    try {
      const api = new Api();
      const res = await api.getSectionDetail({ language: activeLang });
      if (res?.status === 200 && Array.isArray(res.data.results)) {
        const first = res.data.results[0];
        const secWidgets = first?.widgets ?? [];

        if (secWidgets.length > 0) {
          setBoxContent(secWidgets[0]?.widget_contents?.[0] ?? null);
          setTextContent(secWidgets[2]?.widget_contents ?? []);
          setPlaceHolder(secWidgets[3]?.widget_contents[0] ?? "");
          setListCategory(secWidgets[1]?.article_categories ?? []);
          setWidgetId(secWidgets[1]?.id ?? null);
          setNewsDetailPath(
            secWidgets[0]?.widget_contents?.[0]?.article_language?.path ?? "/"
          );
        }
      }
    } catch (err) {
      // silently fail — keep state defaults
    }
  }, [activeLang]);

  useEffect(() => {
    getSectionsDetail();
  }, [getSectionsDetail]);

  const getAllArticles = useCallback(async () => {
    if (!widgetId) return;
    setIsLoading(true);
    try {
      const api = new GlobalApi();
      // gunakan appliedSearchTerm (bukan searchTerm)
      const res = await api.getArticleDetailByYearDescendingPagination(
        widgetId,
        filter.category ?? "",
        PAGE_SIZE,
        appliedSearchTerm ?? "",
        String(page),
        ""
      );

      const results = res?.data?.results ?? [];
      setFilteredArticles(results);
      setArticleCount(res?.data?.count ?? results.length);
    } catch (err) {
      setFilteredArticles([]);
      setArticleCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [widgetId, filter.category, page, appliedSearchTerm]);

  useEffect(() => {
    getAllArticles();
  }, [getAllArticles]);

  const onChangeFilter = (key) => (e) => {
    const value = e.target.value;
    setFilter((f) => ({ ...f, [key]: value }));
    // reset page ke 1 saat ganti filter (fetch akan jalan otomatis karena filter berubah)
    setPage(1);
  };

  // hanya update input
  const onChangeSearch = (e) => setSearchTerm(e.target.value);

  // hanya ketika klik Apply -> search dijalankan (appliedSearchTerm di-update)
  const applyFilter = () => {
    setAppliedSearchTerm(searchTerm);
    setPage(1); // trigget fetch via deps
  };

  const handleChange = (e, p) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setPage(p);
  };

  const renderSingleCategoryListArt = useCallback(
    (item) => {
      const asset = item?.article_assets?.[0]?.asset;
      const thumbnail =
        asset?.thumbnail_file || asset?.file_small || placeHolder?.file;
      const articleTitle = item?.article_languages?.title ?? item.name ?? "-";
      const articleUrl = `${newsDetailPath}/${item?.id}/${item?.article_languages?.slug_title}`;

      return (
        <div
          key={`art-${item.id}`}
          className="group overflow-hidden rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-300"
        >
          <div className="overflow-hidden rounded-t-lg shadow-lg h-48 flex items-center justify-center">
            <img
              title={asset?.description || articleTitle}
              src={thumbnail}
              alt={asset?.description || articleTitle}
              className={clsxm(
                "drop-shadow-lg object-cover w-full h-full transition duration-700 ease-in-out group-hover:scale-110",
                placeHolder?.file === thumbnail ? "h-auto" : ""
              )}
              loading="lazy"
            />
          </div>

          <div className="space-y-2 mt-4 px-3 pb-3">
            <div className="text-left text-sm font-inter text-newDarkBlue">
              {moment(item.date).isValid()
                ? moment(item.date).format("MMMM D, YYYY")
                : ""}
            </div>

            <div className="flex flex-wrap space-x-1">
              {item?.categories?.map((cat) => (
                <p
                  key={cat?.id}
                  className="text-xs font-inter rounded bg-newDarkBlue text-white px-2 py-1 w-fit"
                >
                  {cat?.name}
                </p>
              ))}
            </div>

            <Link className="cursor-pointer block" to={articleUrl || "/"}>
              <div className="font-inter-bold h-full text-lg text-left line-clamp-2 hover:text-newDarkBlue transition-colors">
                {articleTitle}
              </div>
            </Link>
          </div>
        </div>
      );
    },
    [newsDetailPath]
  );

  const renderSkeleton = () => (
    <div className="group overflow-hidden rounded-lg border border-gray-200 shadow-sm animate-pulse">
      <div className="overflow-hidden rounded-t-lg shadow-lg">
        <Skeleton variant="rectangular" width="100%" height={192} />
      </div>
      <div className="space-y-2 mt-5 px-3 pb-3">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="80%" />
      </div>
    </div>
  );

  return (
    <div className="py-[90px] mt-[78px] section-container section-margin-x  relative ">
      <div className="relative z-[-1] bg-[#1a2bs35] rounded-[20px] xl:pb-[40px] mdl:pb-[160px] pb-[120px] px-[40px] pt-[40px] mdl:p-[60px] h-[400px] flex items-center">
        <div
          className="absolute inset-0 rounded-[20px] z-[-1] bg-cover bg-center w-full"
          style={{
            backgroundImage: `url(${boxContent?.assets?.[0]?.asset_file})`,
          }}
        />

        <h1 className="text-white header-style-hero">
          {boxContent?.article_language?.title}
        </h1>
      </div>
      <div
        className="flex mdl:px-[130px] md:px-[60px] px-[20px] -mt-[25px] z-[20]"
        role="search"
        aria-label={textContent?.[0]?.content}
      >
        <input
          type="search"
          placeholder={textContent?.[0]?.content}
          value={searchTerm}
          onChange={onChangeSearch}
          className="flex-grow border rounded-l-lg px-4 py-3 content-subheader-style-non-hero-non-prose placeholder-newDarkBlue/90 focus:outline-none text-newDarkBlue"
          aria-label="Search blog articles"
        />
        <button
          onClick={(e) => {
            applyFilter();
          }}
          type="button"
          className="all-button bg-newDarkBlue !rounded-l-none"
          aria-label={textContent?.[1]?.content}
        >
          {textContent?.[1]?.content}
        </button>
      </div>
      <div className="bg-[#FAF8F5] rounded-lg mdl:px-[60px] p-[40px] mx-auto my-[60px] flex items-center   space-x-[20px] section-margin-x">
        <h2 className="content-header-style-non-hero ">
          {textContent?.[2]?.content}
        </h2>
        <div className="flex flex-wrap gap-4">
          <FormControl sx={{ minWidth: 180 }}>
            <Select
              value={filter.category}
              onChange={onChangeFilter("category")}
              displayEmpty
              sx={{
                backgroundColor: "white",
              }}
              size="small"
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#255262",
                    color: "white",
                  },
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  color: "white",
                  backgroundColor:
                    filter.category === "" ? "#3B82F6" : "transparent",
                  "&:hover": { backgroundColor: "#26577D" },
                }}
              >
                All Categories
              </MenuItem>

              {listCategory?.map((opt) => {
                const isSelected = filter.category === String(opt.id);
                return (
                  <MenuItem
                    key={opt.id}
                    value={opt.id}
                    sx={{
                      color: "white",
                      backgroundColor: isSelected ? "#26577D" : "transparent",
                      "&:hover": { backgroundColor: "#26577D" },
                      py: "10px",
                    }}
                  >
                    {opt.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </div>
      {(appliedSearchTerm || filter?.category) && (
        <div className=" mb-4 flex justify-between items-center">
          <div className="">
            <h3 className="content-header-style-non-hero mb-1">
              {textContent?.[3]?.content}
            </h3>
            <p className="content-subheader-style-non-hero-non-prose">
              {articleCount === 0
                ? textContent?.[5]?.content
                : `${textContent?.[4]?.content} ${
                    page * PAGE_SIZE - PAGE_SIZE + 1
                  } - ${Math.min(
                    page * PAGE_SIZE,
                    articleCount
                  )} of ${articleCount}`}
            </p>
          </div>

          <div className="">
            <button
              onClick={() => {
                setFilter({ category: "" });
                setAppliedSearchTerm("");
                setSearchTerm("");
                setPage(1);
              }}
              className="content-subheader-style-non-hero-non-prose text-newDarkBlue underline"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}{" "}
      <div className="">
        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-6 mdl:gap-[2.5rem]">
          {isLoading
            ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <div key={`skeleton-${index}`}>{renderSkeleton()}</div>
              ))
            : filteredArticles.map((list) => renderSingleCategoryListArt(list))}
        </div>

        {!isLoading && filteredArticles.length === 0 && (
          <div className="text-center py-12 text-gray-500 text-lg">
            {textContent?.[6]?.content}
          </div>
        )}
      </div>
      {pageCount > 1 && (
        <div className="mt-12 flex items-center justify-end gap-x-5 max-w-7xl mx-auto px-2">
          <Pagination
            count={pageCount}
            color="primary"
            size="medium"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handleChange}
            aria-label="Pagination Navigation"
          />
        </div>
      )}
    </div>
  );
}
